import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSignedImageSrc } from '@/hooks/useSignedImageSrc';
import { formatGalleryDayLabel, resolveGalleryItemDate } from '@/pages/ipe/workbench/utils/groupGalleryByDate';

function inferImageFormatLabel(item, imageUrl) {
  const fromName = (item?.title || item?.name || '').match(/\.([a-zA-Z0-9]+)$/);
  if (fromName) return fromName[1].toLowerCase();

  const url = String(imageUrl || item?.url || '').split('?')[0].replace(/\\/g, '/');
  const fromPath = url.match(/\.([a-zA-Z0-9]+)$/);
  if (fromPath) return fromPath[1].toLowerCase();
  return '—';
}

function formatCreatedAt(item) {
  const date = resolveGalleryItemDate(item);
  if (!date) return '—';
  try {
    return format(date, 'yyyy-MM-dd HH:mm');
  } catch {
    return '—';
  }
}

function metaRow(label, value) {
  return (
    <div className="funding-image-space-preview__meta-row">
      <span className="funding-image-space-preview__meta-label">{label}</span>
      <span className="funding-image-space-preview__meta-value">{value}</span>
    </div>
  );
}

export default function OfficialGalleryPreviewModal({
  open,
  item,
  items = [],
  activeTab,
  onClose,
  onMakeSimilar,
}) {
  const [previewItem, setPreviewItem] = useState(item || null);
  const [dimensions, setDimensions] = useState({ width: null, height: null });
  const previewContainerRef = useRef(null);
  const { t } = useTranslation('workbench');

  useEffect(() => {
    setPreviewItem(item || null);
    setDimensions({
      width: item?.width != null ? Number(item.width) : null,
      height: item?.height != null ? Number(item.height) : null,
    });
  }, [item]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const navList = useMemo(() => (Array.isArray(items) ? items.filter(Boolean) : []), [items]);
  const navIndex = useMemo(
    () => navList.findIndex((row) => String(row.id) === String(previewItem?.id)),
    [navList, previewItem?.id],
  );
  const canPreviewPrev = navIndex > 0;
  const canPreviewNext = navIndex >= 0 && navIndex < navList.length - 1;

  const stepPreview = useCallback((delta) => {
    if (navIndex < 0) return;
    const next = navList[navIndex + delta];
    if (!next) return;
    setPreviewItem(next);
    setDimensions({
      width: next.width != null ? Number(next.width) : null,
      height: next.height != null ? Number(next.height) : null,
    });
  }, [navIndex, navList]);

  const signedImageUrl = useSignedImageSrc(previewItem?.url, {
    displayUrl: previewItem?.displayUrl || previewItem?.viewUrl,
    viewUrl: previewItem?.viewUrl,
  });

  if (!open || !previewItem) return null;

  const imageUrl = signedImageUrl;
  const tabLabel = activeTab === 'ai-inspiration'
    ? t('officialGallery.tabAiInspiration')
    : activeTab === 'material'
      ? t('officialGallery.tabMaterial')
      : t('officialGallery.sourceGroup');
  const formatLabel = inferImageFormatLabel(previewItem, imageUrl);
  const promptText = String(previewItem.prompt || '').trim();
  const resolution = dimensions.width && dimensions.height
    ? `${dimensions.width} × ${dimensions.height} px`
    : '—';

  const handleDownload = () => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = previewItem.title || previewItem.id || 'image';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return createPortal(
    <div
      className="funding-image-space-preview-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('imagePreview.detailPreview')}
    >
      <div
        className="funding-image-space-preview-stack"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="funding-image-space-preview-shell">
          <button
            type="button"
            className="funding-image-space-preview-nav"
            aria-label={t('imagePreview.prev')}
            disabled={!canPreviewPrev}
            onClick={() => stepPreview(-1)}
          >
            <ChevronLeft size={22} strokeWidth={2.25} aria-hidden />
          </button>

          <div className="funding-image-space-preview-dialog">
            <button
              type="button"
              className="funding-image-space-preview-close"
              aria-label={t('imagePreview.close')}
              onClick={onClose}
            >
              <X size={20} strokeWidth={2.25} aria-hidden />
            </button>
            <div ref={previewContainerRef} className="funding-image-space-preview__viewport">
              {imageUrl ? (
                <div className="funding-image-space-preview__stage">
                  <img
                    src={imageUrl}
                    alt={previewItem.title || previewItem.id}
                    draggable={false}
                    className="funding-image-space-preview__image"
                    onLoad={(event) => {
                      const el = event.currentTarget;
                      if (!el.naturalWidth || !el.naturalHeight) return;
                      setDimensions({ width: el.naturalWidth, height: el.naturalHeight });
                    }}
                  />
                </div>
              ) : (
                <div className="funding-image-space-preview__placeholder">{t('imagePreview.loadingOrMissing')}</div>
              )}
            </div>

            <aside className="funding-image-space-preview__aside">
              <div className="funding-image-space-preview__title">
                {previewItem.title || previewItem.id || t('imagePreview.unnamed')}
              </div>
              <div className="funding-image-space-preview__section-title">{t('imagePreview.basicInfo')}</div>
              {metaRow(t('imagePreview.format'), formatLabel)}
              {metaRow(t('imagePreview.dimensions'), resolution)}
              {metaRow(t('imagePreview.createdAt'), formatCreatedAt(previewItem))}
              <div className="funding-image-space-preview__meta-row funding-image-space-preview__meta-row--source">
                <span className="funding-image-space-preview__meta-label">{t('imagePreview.source')}</span>
                <span className="funding-image-space-preview__meta-value">
                  <span className="funding-image-space-preview__source-group">{t('officialGallery.sourceGroup')}</span>
                  <span className="funding-image-space-preview__source-value">{tabLabel}</span>
                </span>
              </div>
              <div className="funding-image-space-preview__section-title funding-image-space-preview__section-title--spaced">
                {t('officialGallery.prompt')}
              </div>
              <p className="ipe-official-gallery-preview__prompt">
                {promptText || '—'}
              </p>
              <div className="funding-image-space-preview__section-title funding-image-space-preview__section-title--spaced">
                {t('officialGallery.other')}
              </div>
              {navList.length > 1 && navIndex >= 0
                ? metaRow(t('officialGallery.position'), `${navIndex + 1} / ${navList.length}`)
                : null}
              {metaRow(t('officialGallery.ingestedAt'), formatGalleryDayLabel(resolveGalleryItemDate(previewItem)))}
            </aside>
          </div>

          <button
            type="button"
            className="funding-image-space-preview-nav"
            aria-label={t('imagePreview.next')}
            disabled={!canPreviewNext}
            onClick={() => stepPreview(1)}
          >
            <ChevronRight size={22} strokeWidth={2.25} aria-hidden />
          </button>
        </div>

        <div className="funding-image-space-preview-footer">
          <button
            type="button"
            className="funding-image-space-preview-action funding-image-space-preview-action--download"
            onClick={() => onMakeSimilar?.(previewItem)}
          >
            {t('officialGallery.makeSimilar')}
          </button>
          <button
            type="button"
            className="funding-image-space-preview-action funding-image-space-preview-action--download"
            disabled={!imageUrl}
            onClick={handleDownload}
          >
            <Download size={16} strokeWidth={2} aria-hidden />
            {t('imagePreview.download')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
